import classNames from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { getStrWithoutSpaces } from 'legacy/lut/components/widgets/public/partner360/helpers/getStrWithoutSpaces'
import { IOfferingChange } from 'legacy/lut/components/widgets/public/partner360/offeringsSwithList/OfferingSwitcher'
import { OfferingSwitchConfirmation } from 'legacy/lut/components/widgets/public/partner360/programTypeButtons/components/programTypeButtonsControl/components/offeringSwitchConfirmation/OfferingSwitchConfirmation'
import { AlertBlock } from 'legacy/lut/components/widgets/public/partner360/SignOffAlert'
import { IProgramType, selectPartnerAccess } from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { IApiCommitmentStatus } from 'legacy/lut/services/partner360/commitment/models/apiCommitmentStatus'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Nullable } from 'clickNgo/models/utils'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { Translation } from 'cng-common/features/translations/components/translation'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Switch } from 'core-components/library/checkbox'
import { Loader } from 'core-components/library/loader'
import { Tooltip } from 'core-components/library/tooltip'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { setPublishingMode } from 'core/store/appSlice'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { getAvailableProgramTypes } from 'partner360/helpers/offerings/getAvailableProgramTypes'
import { getOfferingsInfo } from 'partner360/helpers/offerings/getOfferingsInfo'
import { toggleOffering } from 'partner360/helpers/offerings/toggleOffering'
import { Skeleton } from 'shared/components/skeleton'

import { TRANSLATION_KEYS } from '../../TranslationKeys'

import styles from './ProgramTypeButtonsControl.module.scss'

type Props = {
  isStatusLoading: boolean
  startIcon?: string
  programTypes: IProgramType[]
  selectedProgramType: ProgramTypes
  onChange: (value: ProgramTypes) => void
  commitmentStatuses: IApiCommitmentStatus[]
  partnerId: string
  fYear: number
}

export const ProgramTypeButtonsControl: FC<Props> = ({
  isStatusLoading,
  commitmentStatuses,
  programTypes,
  selectedProgramType,
  onChange,
  startIcon,
  partnerId,
  fYear,
}) => {
  const dispatch = useAppDispatch()

  const translationMode = useSelector(selectTranslationMode)
  const isEditMode = useSelector(selectPublishingMode)
  const { isDistributor } = useSelector(selectPartnerAccess)
  const ownerId = useSelector(selectAppOwnerId)

  const isTranslationMode = translationMode === 'edit'

  const [offeringStateForConfirmation, setOfferingStateForConfirmation] =
    useState<Nullable<IOfferingChange>>(null)

  const [loadingItem, setLoadingItem] = useState<Nullable<ProgramTypes>>(null)

  const availableProgramTypes = useMemo(
    () => getAvailableProgramTypes(programTypes),
    [programTypes]
  )

  const { currentOfferings, enabledOfferings } = useMemo(
    () => getOfferingsInfo(availableProgramTypes),
    [availableProgramTypes]
  )

  const showSwitchers = useMemo(() => {
    return isEditMode || enabledOfferings.length === 0
  }, [isEditMode, enabledOfferings])

  const requestConfirmation = useCallback((data: IOfferingChange) => {
    setOfferingStateForConfirmation(data)
  }, [])

  const onToggle = useCallback(
    async ({ offering, flag }: IOfferingChange) => {
      setLoadingItem(offering.programType)

      await toggleOffering({
        fYear,
        offering,
        flag,
        partnerId,
        dispatch,
        commitmentStatuses,
        isDistributor,
        ownerId,
      })
      setLoadingItem(null)
    },
    [commitmentStatuses, dispatch, fYear, isDistributor, ownerId, partnerId]
  )

  const handleToggleOffering = useCallback(
    (data: IOfferingChange) => {
      requestConfirmation(data)
    },
    [requestConfirmation]
  )

  const modalConfirm = useCallback(() => {
    if (!offeringStateForConfirmation) {
      return
    }

    onToggle(offeringStateForConfirmation)
    setOfferingStateForConfirmation(null)
  }, [offeringStateForConfirmation, onToggle])

  const onItemSelect = useCallback(
    (programType: ProgramTypes) => {
      if (programType === ProgramTypes.Consolidated) {
        onChange(programType)
        dispatch(setPublishingMode(false))
        return
      }

      const isEnabled = enabledOfferings.some(
        ({ programType: enabledProgramType }) => enabledProgramType === programType
      )

      if (isEnabled) {
        onChange(programType)
        return
      }

      const offering = currentOfferings.find(
        ({ programType: currentProgramType }) => currentProgramType === programType
      )

      if (offering) {
        requestConfirmation({ offering, flag: true })
      }
    },
    [dispatch, currentOfferings, enabledOfferings, onChange, requestConfirmation]
  )

  const renderTooltipContent = useCallback(
    (acknowledgement: IApiCommitmentStatus | undefined, isConsolidated?: boolean) => {
      if (isConsolidated) {
        return <Translation text={TRANSLATION_KEYS.consolidatedTooltip} />
      }

      if (!acknowledgement) {
        return <></>
      }

      if (acknowledgement.AlertKey) {
        return <AlertBlock className={styles.alert} text={acknowledgement.AlertKey} />
      }

      return <Translation text={acknowledgement.CommitmentStatusKey} />
    },
    []
  )

  const getIcon = useCallback(
    (programType: ProgramTypes, acknowledgement: IApiCommitmentStatus | undefined) => {
      const isEnabled = enabledOfferings.some((it) => it.programType === programType)

      if (programType === ProgramTypes.Consolidated) {
        return <i className={classNames(styles.icon, 'kbase kbase-editor-add-table')} />
      }

      if (isStatusLoading) {
        return <Skeleton width={16} height={16} className={styles.skeletonLoader} />
      }
      if (!isEnabled) {
        return <div className={styles.icon__notApplicable}>N/A</div>
      }
      if (acknowledgement?.CommitmentStatusIcon) {
        return (
          <i
            className={classNames(styles.icon, acknowledgement?.CommitmentStatusIcon)}
            style={{ color: acknowledgement?.CommitmentStatusColor }}
          />
        )
      }
      return null
    },
    [enabledOfferings, isStatusLoading]
  )

  useEffect(() => {
    if (!enabledOfferings.length && selectedProgramType !== ProgramTypes.Consolidated) {
      onChange(ProgramTypes.Consolidated)
    }
  }, [enabledOfferings.length, onChange, selectedProgramType])

  return (
    <div>
      <SegmentedControl
        selectedItem={selectedProgramType}
        backgroudn="bg-default"
        variant="default"
        onItemSelect={onItemSelect}
        useItemKeys
      >
        {currentOfferings.map((offering) => {
          const programType = offering.programType
          const acknowledgement = commitmentStatuses.find((it) => it.ProgramType === programType)
          const isChecked = enabledOfferings.some((it) => it.programType === programType)
          const isLoading = loadingItem === programType

          return (
            <SegmentedControl.Item key={programType} isDisabled={Boolean(loadingItem)}>
              {isTranslationMode ? (
                <Translation span text={acknowledgement?.CommitmentStatusKey || ''} />
              ) : null}

              <div className={styles.buttonContent}>
                <RenderWhen condition={showSwitchers}>
                  {isLoading ? (
                    <Loader dimension={{ width: 25, height: 25 }} />
                  ) : (
                    <Tooltip
                      tipContent={() => (
                        <Translation
                          span
                          text={
                            isChecked
                              ? TRANSLATION_KEYS.offerings.turnOffOffering
                              : TRANSLATION_KEYS.offerings.turnOnOffering
                          }
                        />
                      )}
                    >
                      <Switch
                        disabled={Boolean(loadingItem)}
                        checked={isChecked}
                        size={'sm'}
                        onChange={(flag: boolean) => {
                          handleToggleOffering({ offering, flag })
                        }}
                        className={styles.checkbox}
                      />
                    </Tooltip>
                  )}
                </RenderWhen>

                <RenderWhen condition={Boolean(startIcon)}>
                  <i className={startIcon} />
                </RenderWhen>

                <Translation span text={`jbp-partner-type-${getStrWithoutSpaces(programType)}`} />
                <Tooltip
                  key={programType}
                  tipContent={() => renderTooltipContent(acknowledgement)}
                  disabled={!acknowledgement}
                >
                  {getIcon(programType, acknowledgement)}
                </Tooltip>
              </div>
            </SegmentedControl.Item>
          )
        })}

        <Tooltip
          key={ProgramTypes.Consolidated}
          tipContent={() => renderTooltipContent(undefined, true)}
        >
          <SegmentedControl.Item>
            {isTranslationMode ? (
              <Translation span text={TRANSLATION_KEYS.consolidatedTooltip} />
            ) : null}

            <div className={styles.buttonContent}>
              <RenderWhen condition={Boolean(startIcon)}>
                <i className={startIcon} />
              </RenderWhen>

              <Translation
                span
                text={`jbp-partner-type-${getStrWithoutSpaces(ProgramTypes.Consolidated)}`}
              />

              {getIcon(ProgramTypes.Consolidated, undefined)}
            </div>
          </SegmentedControl.Item>
        </Tooltip>
      </SegmentedControl>

      <OfferingSwitchConfirmation
        onConfirm={modalConfirm}
        onCancel={() => setOfferingStateForConfirmation(null)}
        isShow={offeringStateForConfirmation !== null}
        isDistributor={isDistributor}
        state={offeringStateForConfirmation}
      />
    </div>
  )
}
